import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Collapse,
  Divider,
  Button,
  Paper,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
  Computer as ComputerIcon,
  Timeline as TimelineIcon,
  Security as SecurityIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, AreaChart, Area, Tooltip as RechartsTooltip, CartesianGrid } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useApp } from '../context/AppContext';
import { apiService } from '../services/api';

const ModernDashboard = ({ onNavigate }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { systemMetrics, websocketConnected } = useApp();
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    performance: true,
    system: true,
    activity: false,
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    let interval;
    
    if (realTimeEnabled) {
      interval = setInterval(() => {
        loadDashboardData();
      }, 5000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [realTimeEnabled]);

  const loadDashboardData = async () => {
    try {
      const dashboard = await apiService.getDashboardData();
      
      // Handle different response structures
      const data = dashboard.data || dashboard;
      
      // Create mock historical data since backend doesn't have history endpoints yet
      const mockHistory = Array.from({ length: 10 }, (_, i) => ({
        timestamp: new Date(Date.now() - (9 - i) * 60000).toISOString(),
        value: Math.random() * 100
      }));
      
      setDashboardData({
        // Use the actual data or fallback values
        cpu_percent: data?.cpu_percent || 0,
        memory_percent: data?.memory_percent || 0,
        disk_percent: data?.disk_percent || 0,
        process_count: data?.process_count || 0,
        network_bytes_sent: data?.network_bytes_sent || 0,
        network_bytes_recv: data?.network_bytes_recv || 0,
        load_average: data?.load_average || [0, 0, 0],
        
        // Add computed values
        cpuHistory: mockHistory,
        memoryHistory: mockHistory.map(item => ({
          ...item,
          value: Math.random() * 100
        })),
        network_io: {
          bytes_sent: data?.network_bytes_sent || 0,
          bytes_recv: data?.network_bytes_recv || 0
        },
        cpu_count: 8, // Mock data
        memory_total: 16 * 1024 * 1024 * 1024, // 16GB mock
        memory_used: ((data?.memory_percent || 0) / 100) * 16 * 1024 * 1024 * 1024,
        disk_total: 500 * 1024 * 1024 * 1024, // 500GB mock
      });
    } catch (error) {
      // Set fallback data on error
      setDashboardData({
        cpu_percent: 0,
        memory_percent: 0,
        disk_percent: 0,
        process_count: 0,
        network_bytes_sent: 0,
        network_bytes_recv: 0,
        load_average: [0, 0, 0],
        cpuHistory: [],
        memoryHistory: [],
        network_io: { bytes_sent: 0, bytes_recv: 0 },
        cpu_count: 8,
        memory_total: 16 * 1024 * 1024 * 1024,
        memory_used: 0,
        disk_total: 500 * 1024 * 1024 * 1024,
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const MetricCard = ({ title, value, unit, icon, color, progress, trend, subtitle, chartData }) => (
    <Card sx={{ 
      height: '100%',
      transition: 'all 0.3s ease',
      backgroundColor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
      border: (theme) => theme.palette.mode === 'light' ? '1px solid #d1d5db' : '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: (theme) => theme.palette.mode === 'light' 
        ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        : '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: (theme) => theme.palette.mode === 'light' 
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          : '0 8px 25px -5px rgba(0, 0, 0, 0.4)',
      }
    }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1, sm: 2 } }}>
          <Box sx={{ 
            p: { xs: 1, sm: 1.5 }, 
            borderRadius: 2, 
            background: `linear-gradient(45deg, ${color}20, ${color}40)`,
            border: `1px solid ${color}30`,
          }}>
            {React.cloneElement(icon, { sx: { fontSize: { xs: '1.2rem', sm: '1.5rem' } } })}
          </Box>
          {trend && (
            <Chip 
              icon={<TrendingUpIcon />} 
              label={trend} 
              size="small" 
              color="success" 
              variant="outlined"
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                height: { xs: 24, sm: 32 }
              }}
            />
          )}
        </Box>
        
        <Typography variant="h4" sx={{ 
          fontWeight: 'bold', 
          mb: 0.5,
          fontSize: { xs: '1.5rem', sm: '2.125rem' }
        }}>
          {value}{unit && <span style={{ fontSize: '0.6em', opacity: 0.7 }}>{unit}</span>}
        </Typography>
        
        <Typography variant="body2" sx={{ 
          color: 'text.secondary', 
          mb: 1,
          fontSize: { xs: '0.75rem', sm: '0.875rem' }
        }}>
          {title}
        </Typography>
        
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.7 }}>
            {subtitle}
          </Typography>
        )}
        
        {progress !== undefined && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: (theme) => theme.palette.mode === 'light' 
                  ? 'rgba(0, 0, 0, 0.1)' 
                  : 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(45deg, ${color}, ${color}80)`,
                  borderRadius: 3,
                }
              }}
            />
          </Box>
        )}
        
        {chartData && chartData.length > 0 && (
          <Box sx={{ mt: 2, height: 80 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis 
                  hide
                  dataKey="timestamp"
                />
                <YAxis 
                  hide
                />
                <CartesianGrid 
                  strokeDasharray="2 2" 
                  stroke={theme.palette.mode === 'light' ? '#f0f0f0' : 'rgba(255, 255, 255, 0.05)'} 
                  strokeWidth={0.5}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={2.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const SectionHeader = ({ title, subtitle, section, actions }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: { xs: 2, sm: 3 },
      p: { xs: 1, sm: 2 },
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: 2,
      border: '1px solid rgba(255, 255, 255, 0.1)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, flex: 1 }}>
        <IconButton 
          onClick={() => toggleSection(section)}
          sx={{ color: 'white', p: { xs: 0.3, sm: 1 } }}
          size="small"
        >
          {expandedSections[section] ? <ExpandLessIcon sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }} />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ 
            color: 'white', 
            fontWeight: 600,
            fontSize: { xs: '0.9rem', sm: '1.25rem' },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              display: { xs: 'none', md: 'block' }
            }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      
      {actions && (
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 0.3, sm: 1 },
          alignItems: 'center',
          flexShrink: 0,
          '& .MuiFormControlLabel-root': {
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            margin: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: { xs: '0.7rem', sm: '0.875rem' }
            },
            '& .MuiSwitch-root': {
              width: { xs: 32, sm: 42 },
              height: { xs: 20, sm: 26 },
              '& .MuiSwitch-switchBase': {
                padding: { xs: '2px', sm: '3px' },
                '& .MuiSwitch-thumb': {
                  width: { xs: 16, sm: 20 },
                  height: { xs: 16, sm: 20 }
                }
              }
            }
          },
          '& .MuiIconButton-root': {
            padding: { xs: '4px', sm: '8px' }
          }
        }}>
          {actions}
        </Box>
      )}
    </Box>
  );

  const ChartCard = ({ title, data, dataKey, color, height = 200 }) => {
    return (
    <Card sx={{
      backgroundColor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
      border: (theme) => theme.palette.mode === 'light' ? '1px solid #d1d5db' : '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: (theme) => theme.palette.mode === 'light' 
        ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        : '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
    }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box sx={{ 
          height,
          '& .recharts-cartesian-axis-line': {
            stroke: theme.palette.mode === 'light' ? '#000000 !important' : 'rgba(255, 255, 255, 0.6) !important',
          },
          '& .recharts-cartesian-axis-tick-line': {
            stroke: theme.palette.mode === 'light' ? '#000000 !important' : 'rgba(255, 255, 255, 0.6) !important',
          },
          '& .recharts-cartesian-axis-tick-value': {
            fill: theme.palette.mode === 'light' ? '#000000 !important' : 'rgba(255, 255, 255, 0.6) !important',
          },
          '& .recharts-cartesian-grid-horizontal line, & .recharts-cartesian-grid-vertical line': {
            stroke: theme.palette.mode === 'light' ? '#e5e7eb !important' : 'rgba(255, 255, 255, 0.1) !important',
          }
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data || []}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={theme.palette.mode === 'light' ? '#000000' : 'rgba(255, 255, 255, 0.3)'} 
                strokeWidth={1}
              />
              <XAxis 
                dataKey="timestamp" 
                stroke={theme.palette.mode === 'light' ? '#000000' : '#ffffff'}
                tick={{ fill: theme.palette.mode === 'light' ? '#000000' : '#ffffff', fontSize: 12 }}
                axisLine={{ stroke: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}
                tickLine={{ stroke: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}
              />
              <YAxis 
                stroke={theme.palette.mode === 'light' ? '#000000' : '#ffffff'}
                tick={{ fill: theme.palette.mode === 'light' ? '#000000' : '#ffffff', fontSize: 12 }}
                axisLine={{ stroke: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}
                tickLine={{ stroke: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : 'rgba(26, 31, 58, 0.9)',
                  border: theme.palette.mode === 'light' ? '1px solid #000000' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: theme.palette.mode === 'light' ? '#000000' : '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
              />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={3}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography variant="h6" sx={{ color: 'white' }}>{t('loading_dashboard')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* System Overview */}
      <SectionHeader 
        title={t('system_overview')} 
        subtitle={t('real_time_system_metrics')}
        section="overview"
        actions={[
          <FormControlLabel
            key="realtime"
            control={
              <Switch
                checked={realTimeEnabled}
                onChange={(e) => setRealTimeEnabled(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={t('real_time_updates')}
            sx={{ mr: 2 }}
          />,
          <Tooltip title={t('refresh_data')} key="refresh">
            <IconButton onClick={loadDashboardData} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        ]}
      />

      {/* Connection Status */}
      {!websocketConnected && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('websocket_connection_lost')}
        </Alert>
      )}
      
      <Collapse in={expandedSections.overview}>
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title={t('cpu_usage')}
              value={dashboardData?.cpu_percent?.toFixed(1) || '0.0'}
              unit="%"
              icon={<SpeedIcon sx={{ color: '#667eea' }} />}
              color="#667eea"
              progress={dashboardData?.cpu_percent || 0}
              trend="+2.1%"
              chartData={dashboardData?.cpuHistory?.slice(-10) || [
                { value: 45 }, { value: 52 }, { value: 48 }, { value: 61 }, { value: 55 },
                { value: 67 }, { value: 43 }, { value: 58 }, { value: 50 }, { value: dashboardData?.cpu_percent || 45 }
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title={t('memory_usage')}
              value={dashboardData?.memory_percent?.toFixed(1) || '0.0'}
              unit="%"
              icon={<MemoryIcon sx={{ color: '#f093fb' }} />}
              color="#f093fb"
              progress={dashboardData?.memory_percent || 0}
              subtitle={`${(dashboardData?.memory_used / 1024 / 1024 / 1024)?.toFixed(1) || '0'} ${t('gb_used')}`}
              chartData={dashboardData?.memoryHistory?.slice(-10) || [
                { value: 65 }, { value: 70 }, { value: 68 }, { value: 75 }, { value: 72 },
                { value: 78 }, { value: 71 }, { value: 74 }, { value: 69 }, { value: dashboardData?.memory_percent || 70 }
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title={t('active_processes')}
              value={dashboardData?.process_count || 0}
              icon={<ComputerIcon sx={{ color: '#4facfe' }} />}
              color="#4facfe"
              subtitle={t('running_processes')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title={t('network_io')}
              value={((dashboardData?.network_io?.bytes_sent || 0) / 1024 / 1024).toFixed(1)}
              unit={` ${t('mb_s')}`}
              icon={<NetworkIcon sx={{ color: '#43e97b' }} />}
              color="#43e97b"
              subtitle={t('outbound_traffic')}
              chartData={[
                { value: 0.8 }, { value: 1.2 }, { value: 0.9 }, { value: 1.5 }, { value: 1.1 },
                { value: 1.8 }, { value: 0.7 }, { value: 1.3 }, { value: 1.0 }, { value: ((dashboardData?.network_io?.bytes_sent || 0) / 1024 / 1024) || 1.0 }
              ]}
            />
          </Grid>
        </Grid>
      </Collapse>

      {/* Performance Charts */}
      <SectionHeader 
        title={t('performance_analytics')} 
        subtitle={t('historical_system_performance')}
        section="performance"
      />
      
      <Collapse in={expandedSections.performance}>
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('cpu_usage_over_time')}
              data={dashboardData?.cpuHistory || []}
              dataKey="value"
              color="#667eea"
              height={250}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('memory_usage_over_time')}
              data={dashboardData?.memoryHistory || []}
              dataKey="value"
              color="#f093fb"
              height={250}
            />
          </Grid>
        </Grid>
      </Collapse>

      {/* System Information */}
      <SectionHeader 
        title={t('system_information')} 
        subtitle={t('hardware_environment_details')}
        section="system"
      />
      
      <Collapse in={expandedSections.system}>
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{
              backgroundColor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
              border: (theme) => theme.palette.mode === 'light' ? '1px solid #d1d5db' : '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: (theme) => theme.palette.mode === 'light' 
                ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('hardware')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('cpu_cores')}
                    </Typography>
                    <Typography variant="body2">
                      {dashboardData?.cpu_count || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('total_memory')}
                    </Typography>
                    <Typography variant="body2">
                      {((dashboardData?.memory_total || 0) / 1024 / 1024 / 1024).toFixed(1)} GB
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('disk_space')}
                    </Typography>
                    <Typography variant="body2">
                      {((dashboardData?.disk_total || 0) / 1024 / 1024 / 1024).toFixed(0)} GB
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{
              backgroundColor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
              border: (theme) => theme.palette.mode === 'light' ? '1px solid #d1d5db' : '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: (theme) => theme.palette.mode === 'light' 
                ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('dashboard_environment')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('platform')}
                    </Typography>
                    <Typography variant="body2">
                      Docker
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('python_version')}
                    </Typography>
                    <Typography variant="body2">
                      3.9+
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('websocket')}
                    </Typography>
                    <Chip 
                      label={websocketConnected ? t('connected') : t('disconnected')}
                      color={websocketConnected ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{
              backgroundColor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
              border: (theme) => theme.palette.mode === 'light' ? '1px solid #d1d5db' : '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: (theme) => theme.palette.mode === 'light' 
                ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('quick_actions')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button 
                    startIcon={<CodeIcon />} 
                    variant="outlined" 
                    size="small"
                    onClick={() => onNavigate && onNavigate('sandbox')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {t('open_sandbox')}
                  </Button>
                  <Button 
                    startIcon={<TimelineIcon />} 
                    variant="outlined" 
                    size="small"
                    onClick={() => onNavigate && onNavigate('monitoring')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {t('view_monitoring')}
                  </Button>
                  <Button 
                    startIcon={<SecurityIcon />} 
                    variant="outlined" 
                    size="small"
                    onClick={() => onNavigate && onNavigate('security')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {t('dashboard_security_scan')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
};

export default ModernDashboard;
